import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * TypeAheadSearch — search input with dropdown suggestions
 *
 * Adapted from Sandvik Catalog TypeAheadSearch.jsx
 * Features:
 *   - Keyboard navigation (Arrow keys, Enter, Escape)
 *   - Competitor cross-reference badges (amber)
 *   - Match highlighting (yellow bg)
 *   - "View all results" footer link
 *   - Click-outside detection
 *   - Accessible: aria-autocomplete, aria-expanded, role=listbox
 */

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightMatch(text, query) {
  if (!query || query.length < 2 || !text) return text;
  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-yellow-100 text-carbon-black">{part}</mark>
    ) : part
  );
}

export default function TypeAheadSearch({
  value = '',
  onChange,
  onSearch,
  onSelect,
  suggestions = [],
  loading = false,
  placeholder = 'Search by product',
  minChars = 2,
  maxSuggestions = 8,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = suggestions.slice(0, maxSuggestions);
  const showDropdown = isOpen && value.length >= minChars && (filtered.length > 0 || loading);

  const handleChange = (e) => {
    onChange?.(e.target.value);
    setIsOpen(true);
    setHighlightIndex(-1);
  };

  const handleSelect = (suggestion) => {
    onSelect?.(suggestion);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (highlightIndex >= 0 && filtered[highlightIndex]) {
      handleSelect(filtered[highlightIndex]);
    } else {
      onSearch?.(value);
    }
    setIsOpen(false);
  };

  const handleViewAll = () => {
    onSearch?.(value);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) {
      if (e.key === 'Enter') handleSubmit(e);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        handleSubmit(e);
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightIndex(-1);
        break;
    }
  };

  // Click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        inputRef.current && !inputRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit}>
        <div className="flex w-full">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => value.length >= minChars && setIsOpen(true)}
              placeholder={placeholder}
              className="w-full px-4 py-2 text-sm text-carbon-black bg-white border border-r-0 border-n2 rounded-none
                         placeholder:text-n4 focus:outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue
                         font-sandvik-text"
              aria-label="Search products"
              aria-autocomplete="list"
              aria-controls="search-suggestions"
              aria-expanded={showDropdown}
            />
            {/* Filter icon (like live MRT) */}
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => { onChange?.(''); setIsOpen(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-n4 hover:text-carbon-black"
                aria-label="Clear search"
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="bg-electric-blue hover:bg-eb-3 px-4 py-2 transition-colors rounded-none flex items-center"
            aria-label="Search"
          >
            {loading ? (
              <span className="material-symbols-outlined text-white text-xl animate-spin">progress_activity</span>
            ) : (
              <span className="material-symbols-outlined text-white text-xl">search</span>
            )}
          </button>
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showDropdown && (
        <div
          ref={listRef}
          id="search-suggestions"
          role="listbox"
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-n2 shadow-lg max-h-80 overflow-y-auto z-50"
        >
          {loading ? (
            <div className="flex items-center gap-3 px-4 py-3 text-n5">
              <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
              <span className="text-sm font-sandvik-text">Searching...</span>
            </div>
          ) : filtered.length > 0 ? (
            filtered.map((suggestion, index) => {
              const isCompetitor = suggestion.type === 'competitor';
              return (
                <button
                  key={suggestion.product?.itemCode || index}
                  type="button"
                  onClick={() => handleSelect(suggestion)}
                  onMouseEnter={() => setHighlightIndex(index)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors
                    ${index === highlightIndex ? 'bg-n1' : 'hover:bg-n1'}
                    ${index > 0 ? 'border-t border-n2' : ''}`}
                  role="option"
                  aria-selected={index === highlightIndex}
                >
                  <span className={`material-symbols-outlined text-lg mt-0.5 flex-shrink-0 ${isCompetitor ? 'text-amber-600' : 'text-n4'}`}>
                    {isCompetitor ? 'compare_arrows' : 'precision_manufacturing'}
                  </span>
                  <div className="min-w-0 flex-1">
                    {isCompetitor && suggestion.manufacturer && (
                      <span className="inline-flex items-center text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 mb-1">
                        {suggestion.manufacturer}: {suggestion.partNumber}
                      </span>
                    )}
                    <p className="text-sm text-carbon-black truncate font-sandvik-text">
                      {highlightMatch(suggestion.text || suggestion.product?.description || '', value)}
                    </p>
                    <p className="text-xs text-n5 font-sandvik-text">
                      {isCompetitor ? 'Sandvik: ' : ''}{suggestion.product?.itemCode}
                      {suggestion.product?.category && ` · ${suggestion.product.category}`}
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-sm text-n4 mt-1 flex-shrink-0">arrow_forward</span>
                </button>
              );
            })
          ) : (
            <div className="px-4 py-3 text-sm text-n5 font-sandvik-text">
              No products found for "{value}"
            </div>
          )}

          {/* View all results */}
          {filtered.length > 0 && (
            <button
              type="button"
              onClick={handleViewAll}
              className="w-full px-4 py-3 text-sm text-electric-blue hover:bg-eb-1/20 border-t border-n2 flex items-center justify-center gap-2 font-sandvik-text"
            >
              View all results for "{value}"
              <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
